import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Link,
} from "@heroui/react";
import { plural, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Receipt } from "lucide-react";
import {
  exitChargeStateLabel,
  exitChargeStateOf,
  hasIssuedExitChargeDocument,
  pastCommitmentStatusLabel,
  planTermLabel,
} from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import type {
  ExitChargeDocumentType,
  GetCustomerSubscription,
} from "@/server-types";

type CustomerSubscription =
  GetCustomerSubscription["response"]["customerSubscription"];

type Commitment = CustomerSubscription["commitments"][number];

type CustomerSubscriptionPastCommitmentsProps = {
  commitments: Commitment[];
  durationValue: CustomerSubscription["durationValue"];
  durationUnit: CustomerSubscription["durationUnit"];
  /** Absent while the account cannot issue documents at all. */
  onIssueCharge?: (
    commitmentId: number,
    documentType: ExitChargeDocumentType,
  ) => void;
  isIssuing: boolean;
  /** Absent while the account has no tax register to show a document from. */
  onOpenDocument?: (documentId: string) => void;
};

export function CustomerSubscriptionPastCommitments({
  commitments,
  durationValue,
  durationUnit,
  onIssueCharge,
  isIssuing,
  onOpenDocument,
}: CustomerSubscriptionPastCommitmentsProps) {
  return (
    <div
      data-identifier="subscription-past-commitments"
      className="flex flex-col gap-1"
    >
      <p className="text-default-500 text-tiny">
        {plural(commitments.length, {
          one: "Pretekla vezava",
          two: "Pretekli vezavi",
          few: "Pretekle vezave",
          other: "Pretekle vezave",
        })}
      </p>
      <ul className="flex flex-col gap-1.5">
        {commitments.map((commitment) => (
          <li
            key={commitment.id}
            data-identifier="subscription-past-commitment"
            className="flex flex-col gap-0.5"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-2 text-small">
              <span>
                {planTermLabel(commitment.periods, durationValue, durationUnit)}
                {" · "}
                {t`${commitment.priceFormatted} na obdobje`}
              </span>
              <span className="text-default-500 text-tiny">
                {commitment.firstPeriodStartFormatted}
                {" - "}
                {commitment.lastPeriodEndFormatted}
                {" · "}
                {pastCommitmentStatusLabel(commitment)}
              </span>
            </div>
            <ExitChargeRow
              commitment={commitment}
              onIssueCharge={onIssueCharge}
              isIssuing={isIssuing}
              onOpenDocument={onOpenDocument}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

type ExitChargeRowProps = Pick<
  CustomerSubscriptionPastCommitmentsProps,
  "onIssueCharge" | "isIssuing" | "onOpenDocument"
> & {
  commitment: Commitment;
};

function ExitChargeRow({
  commitment,
  onIssueCharge,
  isIssuing,
  onOpenDocument,
}: ExitChargeRowProps) {
  const state = exitChargeStateOf(commitment);
  if (state === "none") {
    return null;
  }

  const stateLabel = exitChargeStateLabel(state);
  const issuedDocumentId = hasIssuedExitChargeDocument(state)
    ? commitment.exitChargeDocumentId
    : null;

  return (
    <div
      data-identifier="subscription-past-commitment-exit-charge"
      className="flex flex-wrap items-center justify-between gap-x-2 text-tiny"
    >
      <span className="text-default-500">
        {t`Odstopnina`}
        {": "}
        <span className="text-foreground">
          {commitment.exitChargeFormatted}
        </span>
        {stateLabel ? ` · ${stateLabel}` : null}
      </span>
      {issuedDocumentId && onOpenDocument ? (
        <Link
          size="sm"
          className="cursor-pointer text-tiny"
          data-identifier="exit-charge-document-link"
          onPress={() => onOpenDocument(issuedDocumentId)}
        >
          <Trans>Prikaži dokument</Trans>
        </Link>
      ) : null}
      {state === "outstanding" && onIssueCharge ? (
        <Dropdown>
          <DropdownTrigger>
            <Button
              size="sm"
              variant="light"
              isLoading={isIssuing}
              data-identifier="issue-exit-charge-trigger"
              startContent={<Receipt className="h-3 w-3" />}
            >
              <Trans>Izdaj</Trans>
            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label={t`Izdaj odstopnino`}>
            <DropdownItem
              key="estimate"
              data-identifier="issue-exit-charge-estimate"
              onPress={() => onIssueCharge(commitment.id, "estimate")}
            >
              <Trans>Izdaj predračun</Trans>
            </DropdownItem>
            <DropdownItem
              key="invoice"
              data-identifier="issue-exit-charge-invoice"
              onPress={() => onIssueCharge(commitment.id, "invoice")}
            >
              <Trans>Izdaj račun</Trans>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      ) : null}
    </div>
  );
}
